import React, { useState, useCallback, useEffect } from 'react';
import { YoutubeManager } from '../utils/YoutubeManager';
import type { YouTubeVideoItem } from '../types/youtube';
import VideoArchiveFilter, { type VideoArchiveFilters } from '../components/VideoArchiveFilter';
import VideoSearchResultList from '../components/VideoSearchResultList';
import KeywordTagCloud, { type TagWithCount } from '../components/KeywordTagCloud';

const initialFilters: VideoArchiveFilters = {
    dateStart: '',
    dateEnd: '',
    keywords: '',
    freeText: ''
};

function VideoArchive() {
    const [videos, setVideos] = useState<YouTubeVideoItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<VideoArchiveFilters>(initialFilters);
    const [tagCounts, setTagCounts] = useState<TagWithCount[]>([]);
    const [tagCountsLoading, setTagCountsLoading] = useState(true);

    const runSearch = useCallback(async (searchFilters: VideoArchiveFilters) => {
        setLoading(true);
        setError(null);
        try {
            const results = await YoutubeManager.getInstance().searchYouTubeVideos({
                freeText: searchFilters.freeText || undefined,
                keywords: searchFilters.keywords || undefined,
                dateStart: searchFilters.dateStart || undefined,
                dateEnd: searchFilters.dateEnd || undefined
            });
            setVideos(results);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Search failed');
            setVideos([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        runSearch(initialFilters);
    }, [runSearch]);

    useEffect(() => {
        let cancelled = false;
        YoutubeManager.getInstance().fetchChannelVideoTagCounts().then((counts) => {
            if (!cancelled) setTagCounts(counts);
        }).catch(() => {
            if (!cancelled) setTagCounts([]);
        }).finally(() => {
            if (!cancelled) setTagCountsLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    const handleSelectKeyword = useCallback((keyword: string) => {
        const nextFilters = { ...filters, keywords: keyword };
        setFilters(nextFilters);
        runSearch(nextFilters);
    }, [filters, runSearch]);

    return (
        <div className="video-archive-page">
            <div className="page-title">
                <div className="container">
                    <h1>Video Archive</h1>
                </div>
            </div>

            <section className="section">
                <div className="container">
                    <div className="video-archive-layout">
                        <aside className="video-archive-filter-panel">
                            <VideoArchiveFilter
                                filters={filters}
                                onFiltersChange={setFilters}
                                onSearch={runSearch}
                                loading={loading}
                                initialTags={tagCounts.length > 0 ? tagCounts.map((t) => t.tag) : undefined}
                            />
                            <KeywordTagCloud
                                maxTags={15}
                                tagsWithCounts={tagCounts}
                                onSelectKeyword={handleSelectKeyword}
                                loading={tagCountsLoading}
                            />
                        </aside>
                        <div className="video-archive-results-panel">
                            <VideoSearchResultList
                                videos={videos}
                                loading={loading}
                                error={error}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default VideoArchive;
